import { ROUTES } from 'Constants';
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import queryString from 'querystring';
import { FormattedMessage } from 'react-intl';

import { BOOKING_CANCEL_SUCCESS_PAGE_TITLE } from 'ConstantPageNames';
import { SecuredGroups } from 'Models/Security/UserGroups';
import { store } from 'Models/Store';
import { themeStore } from 'Models/ThemeStore';
import { stringNotEmpty } from 'Util/TypeGuards';
import NavigationWrapper from 'Views/Components/Navigation/NavigationWrapper';
import SecuredPage from 'Views/Components/Security/SecuredPage';
import { upperCaseFirst } from 'Util/StringUtils';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import {
	CancellationResult,
	useCancellationResult,
} from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/CancellationResult';

export default function BookingCancelSuccessPage() {
	return (
		<SecuredPage groups={SecuredGroups.create.onlyLoggedIn().groups}>
			<BookingCancelSuccess />
		</SecuredPage>
	);
}

export function BookingCancelSuccess() {
	useEffect(() => {
		document.title = BOOKING_CANCEL_SUCCESS_PAGE_TITLE;
	}, []);

	const location = useLocation();
	const queryParams = queryString.parse(location.search.substring(1));

	const transactionId = queryParams.id?.toString() ?? '';
	const userFirstName = queryParams.userFirstName?.toString() ?? '';
	const userLastName = queryParams.userLastName?.toString() ?? '';
	const bookingId = queryParams.bookingId?.toString() ?? '';
	// Callers pass `type=event|trip` so we know the booking flavour before the bookings
	// fetch returns — lets the heading/spiel render correctly on first paint.
	const typeParam = queryParams.type?.toString();
	// Names may arrive in any casing (e.g. customer entered "JOHN SMITH"); normalise so
	// the spiel always reads "John Smith's …" rather than shouting back at staff.
	const userName =
		`${upperCaseFirst(userFirstName.toLowerCase())} ${upperCaseFirst(userLastName.toLowerCase())}`.trim();

	const result = useCancellationResult(transactionId, bookingId);
	const zeroUsageGiftCertificates = result.giftCertificateUsage;
	// Prefer the URL hint while the bookings fetch is in flight; fall back to the fetched
	// data afterwards so the page still works when navigated to directly without the hint.
	const isEventBooking =
		typeParam === 'event' || (typeParam !== 'trip' && result.bookings.some(b => stringNotEmpty(b.eventName)));

	return (
		<div className="body-content booking-cancel-success">
			<img className="background-img" src={themeStore.bookingCancelSuccessBackgroundImageURL} alt="bg-img" />
			<div className="colour-overlay" />
			<NavigationWrapper className="booking-success__nav" />
			<div className="booking-success__content">
				<div className="content__header">
					<h2>
						{isEventBooking ? (
							<FormattedMessage
								id="booking-cancel-success__title-event"
								defaultMessage="Event booking cancelled"
							/>
						) : (
							<FormattedMessage id="booking-cancel-success__title" defaultMessage="Trip cancelled" />
						)}
					</h2>
					<h5>
						<FormattedMessage
							id="booking-cancel-success__subtitle"
							defaultMessage="Hope to see you another time!"
						/>
					</h5>
				</div>

				<div className="cancel-stack">
					<div className="cancel-main-card">
						<CancellationResult result={result} userName={userName} isEventBooking={isEventBooking} />
					</div>

					<div className="cancel-stack__actions">
						{bookingId && (
							<Link
								className="btn new-style btn--outline btn--grey btn--lg cancel-stack__button"
								to={`/bookings/${bookingId}`}
							>
								<Icon name="arrow_back" useMaterialIcons />
								<span>Back to booking</span>
							</Link>
						)}
						{zeroUsageGiftCertificates?.giftCertificate && (
							<Link
								className="btn new-style btn--outline btn--grey btn--lg cancel-stack__button"
								to={resolveCreditsHref(zeroUsageGiftCertificates.giftCertificate.userId)}
							>
								<Icon name="payments" useMaterialIcons />
								<span>View all credits</span>
							</Link>
						)}
						<Link
							className="btn new-style btn--solid btn--secondary btn--lg cancel-stack__button"
							to="/ferry-schedule"
						>
							<Icon name="event" useMaterialIcons />
							<span>
								<FormattedMessage id="booking-cancel-success__button" defaultMessage="Go to schedule" />
							</span>
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
}

// Staff view a customer's credits at /users/{userId}/credits, customers view their own at
// /account-details/credits. Falls back to the customer path when staff somehow have a
// voucher without a userId attached (shouldn't happen, defensive only).
function resolveCreditsHref(userId?: string): string {
	if (store.isStaff && userId) {
		return ROUTES.USER_DETAIL.url(userId, 'credits');
	}
	return '/account-details/credits';
}
